/**
 * 懒加载管理器
 * - 为所有图片添加懒加载
 * - 确保 scroll-3d section 优先加载
 * - 使用 Intersection Observer API 来高效检测图片可见性
 */

class LazyLoadingManager {
  constructor() {
    this.imageObserver = null;
    this.init();
  }

  init() {
    // 首先优先加载 scroll-3d section
    this.prioritizeScroll3DLoading();
    
    // 初始化图片懒加载
    this.initializeLazyLoading();
  }

  /**
   * 优先加载 scroll-3d section 中的资源
   */
  prioritizeScroll3DLoading() {
    const scroll3dSection = document.querySelector('.scroll-3d-section');
    if (!scroll3dSection) return;

    // 为 scroll-3d canvas 设置较高优先级
    const canvas = scroll3dSection.querySelector('canvas[id^="scroll-3d-canvas"]');
    if (canvas) {
      canvas.setAttribute('importance', 'high');
      console.log('✅ 已标记 scroll-3d canvas 为高优先级');
    }
  }

  /**
   * 初始化图片懒加载
   */
  initializeLazyLoading() {
    // 创建 Intersection Observer 来监听图片可见性
    const observerOptions = {
      root: null,
      rootMargin: '50px', // 在图片进入视口前 50px 就开始加载
      threshold: 0.01
    };

    this.imageObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.loadElement(entry.target);
          this.imageObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // 观察所有需要懒加载的元素
    this.observeLazyElements();
  }

  /**
   * 观察所有需要懒加载的元素
   */
  observeLazyElements() {
    // 观察所有带有 data-src 的图片
    document.querySelectorAll('img[data-src]').forEach((img) => {
      this.imageObserver.observe(img);
    });

    // 观察所有 <picture> 元素内的图片
    document.querySelectorAll('picture img').forEach((img) => {
      if (img.getAttribute('src') !== img.getAttribute('data-src')) {
        this.imageObserver.observe(img);
      }
    });

    // 观察所有背景图片元素（带有 data-bg 属性）
    document.querySelectorAll('[data-bg]').forEach((element) => {
      this.imageObserver.observe(element);
    });

    // 监听 DOM 变化，新增的元素也会被懒加载
    this.observeMutations();
  }

  /**
   * 监听 DOM 变化，自动为新增元素添加懒加载
   */
  observeMutations() {
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // 检查新增的图片
            if (node.tagName === 'IMG' && node.getAttribute('data-src')) {
              this.imageObserver.observe(node);
            }
            // 检查新增元素内的图片
            node.querySelectorAll?.('img[data-src]').forEach((img) => {
              this.imageObserver.observe(img);
            });
          }
        });
      });
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * 加载元素（图片或背景图）
   */
  loadElement(element) {
    if (element.tagName === 'IMG') {
      this.loadImage(element);
    } else if (element.hasAttribute('data-bg')) {
      this.loadBackgroundImage(element);
    }
  }

  /**
   * 加载图片
   */
  loadImage(img) {
    const src = img.getAttribute('data-src');
    const srcset = img.getAttribute('data-srcset');

    if (src) {
      img.src = src;
    }
    if (srcset) {
      img.srcset = srcset;
    }

    // 移除 data-src 属性表示已加载
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');

    // 添加加载完成类
    img.classList.add('lazy-loaded');

    console.log(`📷 图片已加载: ${src}`);
  }

  /**
   * 加载背景图片
   */
  loadBackgroundImage(element) {
    const bgImage = element.getAttribute('data-bg');
    if (bgImage) {
      element.style.backgroundImage = `url('${bgImage}')`;
      element.removeAttribute('data-bg');
      element.classList.add('lazy-loaded');

      console.log(`🖼️ 背景图已加载: ${bgImage}`);
    }
  }
}

// 页面加载完成后初始化懒加载
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new LazyLoadingManager();
  });
} else {
  new LazyLoadingManager();
}
